import {useState} from 'react';

import {Button, Modal, TextArea} from '@/components';
import type {AdminBusinessReferral} from '@/types';

const MIN_REASON_LENGTH = 3;
const MAX_REASON_LENGTH = 500;

export default function DisqualifyReferralModal({
  isOpen,
  referral,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  referral: AdminBusinessReferral;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<boolean>;
}) {
  const [reason, setReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const trimmedReason = reason.trim();
  const isValid =
    trimmedReason.length >= MIN_REASON_LENGTH &&
    trimmedReason.length <= MAX_REASON_LENGTH;

  const handleConfirm = async () => {
    setIsSaving(true);
    try {
      if (await onConfirm(trimmedReason)) {
        onClose();
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-11/12 md:w-6/12 lg:w-4/12 px-4 sm:px-0"
      title="Disqualify referral"
      closeOnOutsideClick
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Referral <span className="font-mono">{referral.code}</span> stops
          earning and its unpaid rewards are voided. Rewards already paid out
          stay paid; clawing those back is a separate action.
        </p>

        <TextArea
          label="Reason"
          name="reason"
          value={reason}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setReason(e.target.value)
          }
          type="text"
          aria-rowspan={4}
          maxLength={MAX_REASON_LENGTH}
          className="w-full isFull"
          placeholder="What was found, and where"
          data-testid="disqualify-reason"
        />

        <div className="flex justify-end gap-3">
          <Button variant="ghost" colorScheme="cyan" onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="red"
            disabled={!isValid}
            isLoading={isSaving}
            loadingText="Disqualifying..."
            onClick={handleConfirm}
            data-testid="disqualify-confirm"
          >
            Disqualify
          </Button>
        </div>
      </div>
    </Modal>
  );
}
